/**
 * Mock twin of the cost-engine validation surfaces that are still served (product brief
 * `docs/features/cost-engine-productionisation/01_product_brief.md` §8,
 * review decisions in `02_glossary_and_live_costing.md`) — the Live costing
 * checks index and its cost-line, cost-centre and product-type views, the
 * check settings and the supersede flow: per-item cost-check state, the
 * cost-centre × component cost-line board and the live costing checks. The
 * variant cost matrix and cost pair pages went with the dictionary admin
 * pages (catalogue management in One, P16).
 *
 * Fixtures are realistic, not exhaustive: six dictionary items span the four
 * cost-check states plus a 40+-variant item, eight cost lines span
 * green/amber/red/grey, and the numbers are chosen to be internally
 * consistent (the cost-line board's check/miss counts roughly match the
 * items citing it). Each item's priced variants and quantities, with the
 * state of the last check at each, also back the catalogue price preview's
 * "checks passed" mark and its "Costed on …" line (`getMockPriceCosting`).
 *
 * Tharstern's own names for cost centres, op codes, cost heads and machines
 * throughout (naming.md — preserve the source domain's terms).
 */

import type { ListResponse } from "../data/adapter";
import type {
  LiveCostingEstateDetail,
  ManufacturingPressConsidered,
} from "../data/manufacturing-setup-types";
import type {
  CheckSettings,
  CheckSettingsUpdateRequest,
  CostCentreValidationIndexEntry,
  CostCheckState,
  CostHead,
  CostLineAffectedItem,
  CostLineCheck,
  CostLineIndexEntry,
  CostLineMoneySummary,
  CostLineRef,
  DictionaryValidationFeedsStatus,
  DictionaryValidationIndexEntry,
  DictionaryValidationListOptions,
  FixedCostOptionChip,
  ProductTypeValidationIndexEntry,
  SupersedeChecksRequest,
  SupersedeChecksResult,
  SupersedeEvent,
  SupersedePreview,
} from "../data/cost-engine-validation-types";
import {
  formatRouteReasonText,
  type RouteReasonCode,
  type RouteReasonParams,
} from "../domain/cost-engine-validation-presentation";
import { getMockCostCentreRef } from "./manufacturing-setup";

const CHECK_WINDOW_WEEKS = 4;
// The routing policy's live version — kept in step with `routing-policy.ts`'s
// own `HISTORY` (version 3) so the preview's "policy v3" and the Routing
// policy page never disagree.
const ROUTING_POLICY_VERSION = 3;

/** A cost centre's "why" in the preview's cost-drilldown line: a closed
 *  route-selection code, its words, the policy version it came from, and the
 *  part it costed. */
interface CostCentreReason {
  code: RouteReasonCode;
  text: string;
  policyVersion: number;
  partSpecLabel?: string;
}

type PressConsidered = ManufacturingPressConsidered;

/** Builds a "why", pre-rendered from a closed `RouteReasonCode`
 *  — the mock never authors free-text whys independent of a code (build
 *  note, `04_handover_2026-09-12.md`). */
function costCentreReason(
  code: RouteReasonCode,
  params: RouteReasonParams = {},
  partSpecLabel?: string,
): CostCentreReason {
  return {
    code,
    text: formatRouteReasonText(code, params),
    policyVersion: ROUTING_POLICY_VERSION,
    partSpecLabel,
  };
}

/** "Costed 10 Sep 04:12" — the single v1 timestamp every price shows (no
 *  engine-version/standards-as-of pair). Exported so other mock-data
 *  modules (catalogue.ts's price preview) share one constant. */
export const COSTED_AT = isoDaysAgo(2, 3, 0);

/** The rolling-validation timer's next hourly slice (`*:17` UTC) after
 *  now, and the one before it — the feed runs hourly since 24 Sep 2026. */
function rollingSliceAt(hoursFromNext: number): string {
  const d = new Date();
  d.setUTCMinutes(17, 0, 0);
  if (d.getTime() <= Date.now()) d.setUTCHours(d.getUTCHours() + 1);
  d.setUTCHours(d.getUTCHours() + hoursFromNext);
  return d.toISOString();
}

function isoDaysAgo(days: number, hour = 9, minute = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  d.setUTCHours(hour, minute, 0, 0);
  return d.toISOString();
}

// ── Cost line registry ──────────────────────────────────────────────────────
// Tharstern's own cost-centre ids/codes/names (naming.md). Keyed by
// `${costCentreId}:${component}` so a check can cite one exactly. costHead is
// Tharstern's own cost-head classification.

/** "5351 5COLXL75LEUV" / "5-Colour XL75 LE UV" — the two `PressConsidered`
 *  fields, sourced the same way. */
function pressRef(costCentreId: number): {
  pressCode: string;
  pressName: string;
} {
  const { code, name } = getMockCostCentreRef(costCentreId);
  return { pressCode: `${costCentreId} ${code}`, pressName: name };
}

/** A check's `_self`, as the backend writes it (`views_module` `cost_check_self`). */
function costCheckSelf(checkId: string): string {
  return `/cost-checks/checks/${checkId}`;
}

function costLine(
  costCentreId: number,
  costHead: CostHead,
  component: string,
): CostLineRef {
  // Code and name always come from manufacturing-setup.ts's cost-centre
  // snapshot — the single source (naming.md: one name per concept, in
  // every layer) — never a second hand-typed copy that can drift from what
  // the Setup and Routing policy pages show for the same cost centre.
  const { code, name } = getMockCostCentreRef(costCentreId);
  return {
    costCentreId,
    costCentreCode: code,
    costCentreName: name,
    costHead,
    component,
    // Includes the code, not just the id — "5351" alone means nothing to
    // Henry; "5351 5COLXL75LEUV" is the same cost centre the Equipment and
    // Setup pages already identify this way (coordinator review round 6).
    label: `${costCentreId} ${code} · ${component}`,
  };
}

/** The cost-centre-less cost line. Whole cost heads carry no cost centre —
 *  our paper, outwork and order-expense lines have none, and Tharstern's
 *  equivalent lines carry none either — and on staging paper is the single
 *  biggest line on the board. The public id uses `0`
 *  (`views_module.actions._cost_line_id.NO_COST_CENTRE_ID`), and there is no
 *  cost-centre page to link to. */
const COST_LINE_PAPER: CostLineRef = {
  costCentreId: 0,
  costCentreCode: "",
  costCentreName: "",
  costHead: "paper",
  component: "Paper",
  label: "No cost centre · Paper",
};

const COST_LINE_5351_INK = costLine(5351, "other_material", "Ink");
// Same cost centre as the ink line above, different component — 5351 also
// runs the brochure cover's litho print, and that line matches exactly
// (only the ink material line mismatches). The by-cost-centre view groups
// both under one 5351 row (coordinator review round 6).
const COST_LINE_5351_PRINTRUN = costLine(5351, "printing", "Print run");
const COST_LINE_803_PRINT = costLine(803, "printing", "Print run");
const COST_LINE_5830_PRINT = costLine(5830, "printing", "Print run");
const COST_LINE_1117_PRINT = costLine(1117, "printing", "Print run");
const COST_LINE_5627_PRINT = costLine(5627, "printing", "Print run");
const COST_LINE_801_FINISHING = costLine(801, "finishing", "Finishing run");
const COST_LINE_835_FINISHING = costLine(835, "finishing", "Finishing run");
// Codes/names/sites match manufacturing-setup.ts's cost centre snapshot
// exactly (5607 HYBRID SHEET at Buckingham, 5609 HYBRID REEL at Buckingham
// — both candidates on product type 44's printing step) so the Setup
// mirror, the step map, and this pair page never disagree about which
// presses exist or where they sit (Read it as Henry: mock data internally
// consistent across pages).
const COST_LINE_5607_RUN = costLine(5607, "printing", "Run");
const COST_LINE_5609_RUN = costLine(5609, "printing", "Run");

interface CostLineFixture {
  costLine: CostLineRef;
  health: CostCheckState;
  productTypesAffected: number[];
  totalCheckCount: number;
  missCount: number;
  /** Summed over the cost line's checks. `null` for a never-checked cost
   *  line — there is nothing to sum, not a sum of zero. */
  ourMoneyPence: number | null;
  tharsternMoneyPence: number | null;
  lastCheckedAt: string | null;
  standardsRows: string[];
  /** Check generator seed — plausible dictionary/variant refs to cite. */
  checkSeeds: Array<{
    dictionaryItemId: string;
    dictionaryItemName: string;
    variantId: string;
    variantLabel: string;
    productTypeId: number;
  }>;
  /**
   * Explicit per-miss Δ values (pence, ours − Tharstern), in generation
   * order. Only set where the board's worked example needs a realistic mix
   * of over/under checks rather than one uniform delta; length must equal
   * `missCount`. Falls back to an even split of the aggregate delta across
   * `missCount` checks when omitted.
   */
  missDeltasPence?: number[];
}

const COST_LINE_FIXTURES: CostLineFixture[] = [
  {
    costLine: COST_LINE_PAPER,
    health: "amber",
    productTypesAffected: [1],
    totalCheckCount: 18,
    missCount: 3,
    ourMoneyPence: 54000,
    tharsternMoneyPence: 54600,
    lastCheckedAt: isoDaysAgo(1),
    standardsRows: ["Sheet size × sheets × stock price, from the paper table"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-brochure-cover-text",
        dictionaryItemName: "Brochure Cover & Text",
        variantId: "bct-1",
        variantLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
        productTypeId: 1,
      },
    ],
  },
  {
    costLine: COST_LINE_5351_INK,
    health: "red",
    productTypesAffected: [1],
    totalCheckCount: 30,
    missCount: 9,
    ourMoneyPence: 30000,
    tharsternMoneyPence: 36100,
    lastCheckedAt: isoDaysAgo(2),
    standardsRows: ["Ink coverage × sheet area, litho UV 5-colour band"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-brochure-cover-text",
        dictionaryItemName: "Brochure Cover & Text",
        variantId: "bct-1",
        variantLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
        productTypeId: 1,
      },
    ],
    // 2 over, 7 under — a realistic mix rather than one uniform miss —
    // summing exactly to the aggregate Δ above (30000 − 36100 = −6100).
    missDeltasPence: [410, 150, -1230, -1100, -980, -950, -850, -800, -750],
  },
  {
    costLine: COST_LINE_5351_PRINTRUN,
    health: "green",
    productTypesAffected: [1],
    totalCheckCount: 24,
    missCount: 0,
    ourMoneyPence: 24000,
    tharsternMoneyPence: 24000,
    lastCheckedAt: isoDaysAgo(1),
    standardsRows: ["Run speed band XL UV × sheet count, litho 5-colour"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-brochure-cover-text",
        dictionaryItemName: "Brochure Cover & Text",
        variantId: "bct-1",
        variantLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
        productTypeId: 1,
      },
    ],
  },
  {
    costLine: COST_LINE_801_FINISHING,
    health: "amber",
    productTypesAffected: [63],
    totalCheckCount: 22,
    missCount: 3,
    ourMoneyPence: 22000,
    tharsternMoneyPence: 23400,
    lastCheckedAt: isoDaysAgo(0, 11, 0),
    standardsRows: [
      "Guillotine setup + run, 3-side trim band",
      "Guillotine setup + run, multi-up card cutting band",
    ],
    checkSeeds: [
      {
        dictionaryItemId: "dict-business-cards",
        dictionaryItemName: "Business cards",
        variantId: "bc-1",
        variantLabel: "350gsm silk, standard",
        productTypeId: 63,
      },
    ],
  },
  {
    costLine: COST_LINE_5607_RUN,
    health: "grey",
    productTypesAffected: [44],
    totalCheckCount: 0,
    missCount: 0,
    ourMoneyPence: null,
    tharsternMoneyPence: null,
    lastCheckedAt: null,
    standardsRows: ["Hybrid press run-speed band (not yet captured)"],
    checkSeeds: [],
  },
  {
    costLine: COST_LINE_5609_RUN,
    health: "grey",
    productTypesAffected: [44],
    totalCheckCount: 0,
    missCount: 0,
    ourMoneyPence: null,
    tharsternMoneyPence: null,
    lastCheckedAt: null,
    standardsRows: ["Hybrid reel run-speed band (not yet captured)"],
    checkSeeds: [],
  },
  {
    costLine: COST_LINE_5830_PRINT,
    health: "green",
    productTypesAffected: [36, 77],
    totalCheckCount: 96,
    missCount: 2,
    ourMoneyPence: 96000,
    tharsternMoneyPence: 96340,
    lastCheckedAt: isoDaysAgo(0, 8, 30),
    standardsRows: ["Run speed band, Ricoh Pro C9500 × sheet count"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-perfect-bound-book",
        dictionaryItemName: "Perfect Bound Book",
        variantId: "pbb-1",
        variantLabel: "A5 214pp, 300gsm cover matt lam, 90gsm text",
        productTypeId: 36,
      },
    ],
  },
  {
    costLine: COST_LINE_1117_PRINT,
    health: "green",
    productTypesAffected: [36],
    totalCheckCount: 64,
    missCount: 1,
    ourMoneyPence: 64000,
    tharsternMoneyPence: 64120,
    lastCheckedAt: isoDaysAgo(1),
    standardsRows: ["Run speed band, Ricoh Versafire V1 × sheet count"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-perfect-bound-book",
        dictionaryItemName: "Perfect Bound Book",
        variantId: "pbb-2",
        variantLabel: "A4 128pp, 250gsm cover gloss lam, 100gsm text",
        productTypeId: 36,
      },
    ],
  },
  {
    costLine: COST_LINE_5627_PRINT,
    health: "green",
    productTypesAffected: [77],
    totalCheckCount: 30,
    missCount: 0,
    ourMoneyPence: 30000,
    tharsternMoneyPence: 30000,
    lastCheckedAt: isoDaysAgo(0, 9, 0),
    standardsRows: ["Indigo click rate × sheet count"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-letterheads",
        dictionaryItemName: "Letterheads",
        variantId: "lh-2",
        variantLabel: "120gsm woven, full colour",
        productTypeId: 77,
      },
    ],
  },
  {
    costLine: COST_LINE_835_FINISHING,
    health: "green",
    productTypesAffected: [77],
    totalCheckCount: 20,
    missCount: 0,
    ourMoneyPence: 20000,
    tharsternMoneyPence: 20000,
    lastCheckedAt: isoDaysAgo(3),
    standardsRows: ["KH82 fold run-speed band × sheet count"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-letterheads",
        dictionaryItemName: "Letterheads",
        variantId: "lh-1",
        variantLabel: "100gsm laid, 1-colour",
        productTypeId: 77,
      },
    ],
  },
  {
    costLine: COST_LINE_803_PRINT,
    health: "green",
    productTypesAffected: [1, 36],
    totalCheckCount: 58,
    missCount: 1,
    ourMoneyPence: 58000,
    tharsternMoneyPence: 58090,
    lastCheckedAt: isoDaysAgo(2),
    standardsRows: [
      "Run speed band L1 × sheet count, litho 5-colour",
      "Run speed band L1 × sheet count, litho 4-colour",
    ],
    checkSeeds: [
      {
        dictionaryItemId: "dict-brochure-cover-text",
        dictionaryItemName: "Brochure Cover & Text",
        variantId: "bct-1",
        variantLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
        productTypeId: 1,
      },
      {
        dictionaryItemId: "dict-perfect-bound-book",
        dictionaryItemName: "Perfect Bound Book",
        variantId: "pbb-1",
        variantLabel: "A5 214pp, 300gsm cover matt lam, 90gsm text",
        productTypeId: 36,
      },
    ],
  },
];

const costLineFixtureKey = (r: CostLineRef) =>
  `${r.costCentreId}:${r.component}`;
const costLineFixturesByKey = new Map(
  COST_LINE_FIXTURES.map((f) => [costLineFixtureKey(f.costLine), f]),
);

/** The URL-encoded `"{costCentreId}:{component}"` cost-line id — the same
 *  codec as `routing/routes.ts`'s `encodeCostLineId`/`decodeCostLineId`. */
function encodedCostLineId(r: CostLineRef): string {
  return encodeURIComponent(costLineFixtureKey(r));
}

function costLineSelf(r: CostLineRef): string {
  return `/views/live-costing/cost-lines/${encodedCostLineId(r)}`;
}

function costLineChecksUrl(r: CostLineRef): string {
  return `/views/live-costing/cost-lines/${encodedCostLineId(r)}/checks`;
}

function costCentreSetupUrl(costCentreId: number): string {
  return `/views/manufacturing/cost-centres/${costCentreId}`;
}

/** `null` for the cost-centre-less line (id `0`): there is no cost centre to
 *  navigate to, and the setup route would 404 on the sentinel. */
function costCentreUrlFor(costCentreId: number): string | null {
  return costCentreId === 0 ? null : costCentreSetupUrl(costCentreId);
}

// ── Dictionary item fixtures ─────────────────────────────────────────────

interface CellSpec {
  quantity: number;
  /** `null` on a grey cell: a price with no check has no figure — see
   *  `docs/features/cost-engine-productionisation/08_decision_matrix_shows_checked_figures.md`. */
  costPence: number | null;
  // A fixture cell is always checked, being checked or never checked: never a
  // cached Tharstern estimate.
  state: CostCheckState | "checking";
  /** Set when this exact price (item × variant × quantity) is in the
   *  rolling validation set — "pinned" (an admin chose it) or "scheduled"
   *  (the scheduler picked it to cover a gap). Omit when the price is not
   *  in the set; there is no coarser item-level flag. */
  origin?: "pinned" | "scheduled";
  /** Tharstern's figure for this cell, when it differs from `costPence`.
   *  Defaults to an exact match (green) when omitted. */
  tharsternPence?: number;
}

interface VariantSpec {
  variantId: string;
  displayLabel: string;
  isActive?: boolean;
  /** Set on a fixed-cost corner (spec 03 rule (d)): the same variant with
   *  these options switched on, a price of its own. */
  fixedCostOptions?: FixedCostOptionChip[];
  cells: CellSpec[];
}

const PDF_PROOF_ON: FixedCostOptionChip[] = [
  { fieldKey: "Proofing", optionCode: "pdf-proofing", label: "PDF proof" },
];

interface DictionaryValidationFixture {
  dictionaryItemId: string;
  name: string;
  productTypeId: number;
  costCheckState: CostCheckState;
  mismatchingCostLine: CostLineRef | null;
  pennyExactPct: number | null;
  within5PctPct: number | null;
  lastCheckedAt: string | null;
  usedByCount: number;
  variants: VariantSpec[];
  /** The "why" per cost centre the engine chose, keyed by costCentreId —
   *  pre-rendered via `costCentreReason()` above so every entry traces to
   *  exactly one `RouteReasonCode`. A press cost centre's entry carries its
   *  own `partSpecLabel`. */
  costCentreReasons: Record<number, CostCentreReason>;
  pressesConsidered: PressConsidered[];
}

function countRollingSetCells(fixture: DictionaryValidationFixture): number {
  return fixture.variants
    .flatMap((v) => v.cells)
    .filter((c) => c.origin != null).length;
}

const FIXTURES: DictionaryValidationFixture[] = [
  // 1. Perfect Bound Book — PT36 — live, matches throughout.
  {
    dictionaryItemId: "dict-perfect-bound-book",
    name: "Perfect Bound Book",
    productTypeId: 36,
    costCheckState: "green",
    mismatchingCostLine: null,
    pennyExactPct: 74,
    within5PctPct: 99,
    lastCheckedAt: isoDaysAgo(0, 8, 30),
    usedByCount: 3,
    variants: [
      {
        variantId: "pbb-1",
        displayLabel: "A5 214pp, 300gsm cover matt lam, 90gsm text",
        cells: [
          {
            quantity: 100,
            costPence: 48200,
            state: "green",
          },
          {
            quantity: 250,
            costPence: 45800,
            tharsternPence: 45820,
            state: "green",
            origin: "scheduled",
          },
          {
            quantity: 500,
            costPence: 89400,
            state: "green",
          },
        ],
      },
      {
        variantId: "pbb-2",
        displayLabel: "A4 128pp, 250gsm cover gloss lam, 100gsm text",
        cells: [
          {
            quantity: 100,
            costPence: 41300,
            state: "green",
          },
          {
            quantity: 250,
            costPence: 52700,
            state: "green",
          },
          {
            quantity: 500,
            costPence: 74600,
            state: "green",
          },
        ],
      },
    ],
    costCentreReasons: {
      803: costCentreReason(
        "cheapest_suitable",
        { suitableCount: 2 },
        "Text · 90 gsm",
      ),
      // Three finishing cost centres under one head, each with its own why —
      // never a single shared "finishing" reason.
      835: costCentreReason("step_map_default"),
      801: costCentreReason("only_suitable", { kind: "cost_centre" }),
      // The fixture's one genuine `site_rule` reason (coordinator review
      // round 3) — the binder is Exeter-only per Routing policy's own
      // feasible-sites table, not a cost/step-map choice.
      5317: costCentreReason(
        "site_rule",
        { site: "Exeter" },
        "Book block · 214pp perfect bind",
      ),
      // Plate-making follows the chosen press's own site — only one
      // suitable plate room on that site.
      800: costCentreReason("only_suitable", { kind: "cost_centre" }),
    },
    pressesConsidered: [
      {
        ...pressRef(803),
        site: "Exeter",
        outcome: "chosen",
        reason:
          "cheapest of 2 suitable presses — also keeps printing on the same site as this job's Exeter-only perfect binder",
      },
      {
        ...pressRef(5830),
        site: "Buckingham",
        outcome: "suitable_not_chosen",
        reason:
          "suitable but not cheapest — Buckingham adds carriage to Exeter finishing",
      },
    ],
  },

  // 2. Brochure Cover & Text — PT1 — mismatching cost line 5351 · Ink.
  {
    dictionaryItemId: "dict-brochure-cover-text",
    name: "Brochure Cover & Text",
    productTypeId: 1,
    costCheckState: "red",
    mismatchingCostLine: COST_LINE_5351_INK,
    pennyExactPct: 41,
    within5PctPct: 92,
    lastCheckedAt: isoDaysAgo(2),
    usedByCount: 5,
    variants: [
      {
        variantId: "bct-1",
        displayLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
        cells: [
          {
            // Same -6.1% ratio at every quantity — small on any one line,
            // but systematic across the whole item, which is what makes
            // this cost line a mismatch rather than an amber one-off.
            quantity: 250,
            costPence: 38900,
            tharsternPence: 41429,
            state: "red",
            origin: "pinned",
          },
          {
            quantity: 500,
            costPence: 75300,
            tharsternPence: 79278,
            state: "red",
            origin: "pinned",
          },
          {
            quantity: 1000,
            costPence: 98700,
            tharsternPence: 105116,
            state: "red",
          },
        ],
      },
      {
        variantId: "bct-2",
        displayLabel: "Cover 300gsm uncoated + Text 150gsm, 4pp + 8pp",
        cells: [
          { quantity: 250, costPence: 34500, state: "green" },
          { quantity: 500, costPence: 55800, state: "green" },
          { quantity: 1000, costPence: 89100, state: "green" },
        ],
      },
    ],
    costCentreReasons: {
      // The cover and text presses are two different cost centres under
      // one "printing" head, each with its own why — never a single shared
      // "printing" reason (the same distinction the finishing example
      // makes: a binder and a guillotine, or here, two different presses).
      5351: costCentreReason(
        "cheapest_suitable",
        { suitableCount: 2 },
        "Cover · 250 gsm silk · 4/0",
      ),
      5830: costCentreReason(
        "cheapest_suitable",
        { suitableCount: 1 },
        "Text · 130 gsm silk · 4/0",
      ),
      // Stitching is feasible at both sites (Routing policy's own
      // feasible-sites table), so this is the step map's default choice,
      // not a site rule (same reasoning as fixture 1's guillotine).
      830: costCentreReason("step_map_default"),
      // Plate-making follows the cover press's own site — only one
      // suitable plate room on that site.
      800: costCentreReason("only_suitable", { kind: "cost_centre" }),
    },
    pressesConsidered: [
      {
        ...pressRef(5351),
        site: "Exeter",
        outcome: "chosen",
        reason: "cheapest suitable press for the cover",
      },
      {
        ...pressRef(5830),
        site: "Buckingham",
        outcome: "chosen",
        reason: "cheapest suitable press for the text",
      },
      {
        ...pressRef(803),
        site: "Exeter",
        outcome: "suitable_not_chosen",
        reason: "suitable for the cover but not the cheapest",
      },
    ],
  },

  // 3. Business cards — PT63 — small difference on 801 finishing run; bc-2
  // mismatches on paper because Tharstern substitutes its stock.
  {
    dictionaryItemId: "dict-business-cards",
    name: "Business cards",
    productTypeId: 63,
    costCheckState: "red",
    mismatchingCostLine: COST_LINE_PAPER,
    pennyExactPct: 68,
    within5PctPct: 99,
    lastCheckedAt: isoDaysAgo(0, 11, 0),
    usedByCount: 8,
    variants: [
      {
        variantId: "bc-1",
        displayLabel: "350gsm silk, standard",
        cells: [
          {
            quantity: 250,
            costPence: 3200,
            tharsternPence: 3328,
            state: "amber",
          },
          {
            quantity: 500,
            costPence: 5000,
            tharsternPence: 5060,
            state: "amber",
          },
          {
            quantity: 1000,
            costPence: 7900,
            state: "green",
          },
        ],
      },
      {
        variantId: "bc-2",
        displayLabel: "400gsm uncoated, soft-touch lam",
        // Tharstern's echo substitutes the stock on every quantity, so every
        // check is red on paper — the same substitution the Mappings tab
        // shows on this option's Tharstern leaf.
        cells: [
          {
            quantity: 250,
            costPence: 4100,
            tharsternPence: 4190,
            state: "red",
            origin: "pinned",
          },
          {
            quantity: 500,
            costPence: 6200,
            tharsternPence: 6390,
            state: "red",
          },
          {
            quantity: 1000,
            costPence: 9800,
            tharsternPence: 10100,
            state: "red",
          },
        ],
      },
    ],
    costCentreReasons: {
      803: costCentreReason(
        "cheapest_suitable",
        { suitableCount: 3 },
        "Card · 350 gsm silk · 4/4",
      ),
      // Guillotine trim is feasible at both sites (Routing policy's own
      // per-step feasible-sites table), so this is the step map's default
      // choice, not a site rule.
      801: costCentreReason("step_map_default"),
      // Lamination (bc-2's soft-touch finish) — only one suitable press in
      // this part's candidate list.
      814: costCentreReason("only_suitable", { kind: "cost_centre" }),
      // Plate-making follows the chosen press's own site — only one
      // suitable plate room on that site.
      800: costCentreReason("only_suitable", { kind: "cost_centre" }),
    },
    // The mandated worked example (design review, 02_glossary_and_live_costing.md):
    // a tie between 803 and 5351 at £37.00, broken by the Exeter site rule.
    pressesConsidered: [
      {
        ...pressRef(803),
        site: "Exeter",
        outcome: "chosen",
        reason:
          "cheapest of 3 suitable presses; tied with 5351 — Exeter site-coherence rule broke the tie",
      },
      {
        ...pressRef(5351),
        site: "Exeter",
        outcome: "suitable_not_chosen",
        reason: "tied on cost with 803 — the tie-break rule favoured 803",
      },
      {
        ...pressRef(5830),
        site: "Buckingham",
        outcome: "suitable_not_chosen",
        reason: "suitable but not the cheapest",
      },
      {
        ...pressRef(1117),
        site: "Exeter",
        outcome: "excluded",
        reason: "not in the part's press list",
      },
      {
        ...pressRef(5627),
        site: "Exeter",
        outcome: "excluded",
        reason: "sheet below minimum feed",
      },
    ],
  },

  // 4. Wide format panels — PT44/33 — not yet checked (hybrid press not priceable, hybrid reel never checked).
  {
    dictionaryItemId: "dict-wide-format-panels",
    name: "Wide format panels",
    productTypeId: 44,
    costCheckState: "grey",
    mismatchingCostLine: null,
    pennyExactPct: null,
    within5PctPct: null,
    lastCheckedAt: null,
    usedByCount: 2,
    variants: [
      {
        variantId: "wfp-1",
        displayLabel: "Foamex panel 5mm, 594×841mm",
        cells: [
          {
            quantity: 1,
            costPence: null,
            state: "grey",
          },
          {
            quantity: 5,
            costPence: null,
            state: "grey",
          },
          {
            quantity: 10,
            costPence: null,
            state: "grey",
          },
        ],
      },
      {
        variantId: "wfp-2",
        displayLabel: "Correx panel 4mm, 594×841mm",
        cells: [
          {
            quantity: 1,
            costPence: null,
            state: "grey",
          },
          {
            quantity: 5,
            costPence: null,
            state: "grey",
          },
          {
            quantity: 10,
            costPence: null,
            state: "grey",
          },
        ],
      },
    ],
    // No lines to compare (never priced from spec), so no cost centre has a
    // sub-header to attach a reason to — the Calculation table renders "Not
    // costed yet" instead.
    costCentreReasons: {},
    pressesConsidered: [
      {
        ...pressRef(5607),
        site: "Buckingham",
        outcome: "excluded",
        reason: "hybrid-press value not yet captured — routing withheld",
      },
      {
        ...pressRef(5609),
        site: "Buckingham",
        outcome: "excluded",
        reason: "never validated",
      },
    ],
  },

  // 5. Letterheads — PT77 — live, matches throughout.
  {
    dictionaryItemId: "dict-letterheads",
    name: "Letterheads",
    productTypeId: 77,
    costCheckState: "green",
    mismatchingCostLine: null,
    pennyExactPct: 76,
    within5PctPct: 99,
    lastCheckedAt: isoDaysAgo(0, 9, 0),
    usedByCount: 4,
    variants: [
      {
        variantId: "lh-1",
        displayLabel: "100gsm laid, 1-colour",
        cells: [
          {
            quantity: 250,
            costPence: 4200,
            state: "green",
            origin: "scheduled",
          },
          {
            quantity: 500,
            costPence: 6100,
            state: "green",
          },
          {
            quantity: 1000,
            costPence: 9400,
            state: "green",
          },
        ],
      },
      {
        variantId: "lh-2",
        displayLabel: "120gsm woven, full colour",
        cells: [
          {
            quantity: 250,
            costPence: 6800,
            state: "green",
          },
          {
            quantity: 500,
            costPence: 11400,
            state: "green",
          },
          {
            quantity: 1000,
            costPence: 14900,
            state: "green",
          },
        ],
      },
      {
        // A fixed-cost corner (spec 03 rule (d)): lh-1 with its PDF proof
        // switched on, in the rolling set at 250 only — the plain lh-1:250
        // total plus the proof's 952p job-level line.
        variantId: "lh-1",
        displayLabel: "100gsm laid, 1-colour",
        fixedCostOptions: PDF_PROOF_ON,
        cells: [
          {
            quantity: 250,
            costPence: 5152,
            state: "green",
            origin: "scheduled",
          },
          {
            quantity: 500,
            costPence: null,
            state: "grey",
          },
          {
            quantity: 1000,
            costPence: null,
            state: "grey",
          },
        ],
      },
    ],
    costCentreReasons: {
      5627: costCentreReason(
        "indigo_option",
        {},
        "Sheet · 120 gsm woven · 4/0",
      ),
      835: costCentreReason("step_map_default"),
    },
    pressesConsidered: [
      {
        ...pressRef(5627),
        site: "Exeter",
        outcome: "chosen",
        reason:
          "only suitable press — Indigo flag resolved on this dictionary item",
      },
    ],
  },

  // 6. Greeting cards — PT91 — a 40+-variant item, to exercise the item
  // page's variant-matrix search/filter/paging (design review,
  // 02_glossary_and_live_costing.md). Mostly matching, generated rather than
  // hand-listed — 46 variants × 2 quantities, a handful pinned.
  buildManyVariantsFixture(),

  // 7. Promotional postcards — PT22 — green, no cost-line mismatch.
  // Minimal — this fixture is read by the estate/items list only, never
  // opened as a detail page, so the matrix/pair/checklist fields are empty.
  {
    dictionaryItemId: "dict-promotional-postcards",
    name: "Promotional postcards",
    productTypeId: 22,
    costCheckState: "green",
    mismatchingCostLine: null,
    pennyExactPct: 88,
    within5PctPct: 100,
    lastCheckedAt: isoDaysAgo(1, 9, 15),
    usedByCount: 1,
    variants: [],
    costCentreReasons: {},
    pressesConsidered: [],
  },
];

/** Generates the 40+-variant "Greeting cards" fixture — one variant per
 *  card size/stock combination, each with two quantity columns, almost all
 *  matching. Kept generated rather than hand-listed (46 near-identical
 *  variants would be pure noise to hand-author and review). */
function buildManyVariantsFixture(): DictionaryValidationFixture {
  const sizes = ["A6", "A5", "5x7in", "Square 148mm", "DL"];
  const stocks = [
    "300gsm silk",
    "300gsm uncoated",
    "350gsm recycled",
    "270gsm gloss lam",
    "270gsm matt lam",
    "320gsm textured",
    "300gsm pearlescent",
    "280gsm foil-block",
    "300gsm silk soft-touch",
  ];
  const variants: VariantSpec[] = [];
  let index = 0;
  for (const size of sizes) {
    for (const stock of stocks) {
      index += 1;
      const isSmallDifference = index === 7 || index === 23;
      variants.push({
        variantId: `gc-${index}`,
        displayLabel: `${size}, ${stock}`,
        cells: [
          {
            quantity: 100,
            costPence: 2200 + index * 15,
            state: "green",
            origin: index === 1 || index === 20 ? "scheduled" : undefined,
          },
          {
            quantity: 250,
            costPence: 3400 + index * 22,
            state: isSmallDifference ? "amber" : "green",
            tharsternPence: isSmallDifference
              ? 3400 + index * 22 + 60
              : undefined,
          },
        ],
      });
    }
  }
  return {
    dictionaryItemId: "dict-greeting-cards",
    name: "Greeting cards",
    productTypeId: 91,
    costCheckState: "amber",
    mismatchingCostLine: null,
    pennyExactPct: 88,
    within5PctPct: 100,
    lastCheckedAt: isoDaysAgo(0, 10, 0),
    usedByCount: 3,
    variants,
    costCentreReasons: {
      5627: costCentreReason("indigo_option", {}, "Card · 300 gsm silk · 4/4"),
    },
    pressesConsidered: [
      {
        ...pressRef(5627),
        site: "Exeter",
        outcome: "chosen",
        reason:
          "only suitable press — Indigo flag resolved on this dictionary item",
      },
    ],
  };
}

const fixturesById = new Map(FIXTURES.map((f) => [f.dictionaryItemId, f]));

/** Rolling prices quarantined after a Tharstern timeout (spec 03 §5.1 step
 *  4c), by item. One on the perfect bound book keeps the boards' counts in
 *  every mock-backed page and story; the costs themselves are on Pollards
 *  (in-house)'s record in One. */
const quarantinedByItem = new Map<string, { rollingPriceId: string }[]>([
  ["dict-perfect-bound-book", [{ rollingPriceId: "rp-pbb-2-250" }]],
]);

function quarantinedPricesFor(
  dictionaryItemId: string,
): { rollingPriceId: string }[] {
  return [...(quarantinedByItem.get(dictionaryItemId) ?? [])];
}

// ── Reads ────────────────────────────────────────────────────────────────

/** Ours/Tharstern from the first checked cell — the cost-check badge's
 *  tooltip triplet on the "by dictionary item" index. `null` when nothing on
 *  the item has ever been checked. */
function lastCheckForFixture(
  f: DictionaryValidationFixture,
): { oursPence: number; tharsternPence: number } | null {
  for (const cell of f.variants.flatMap((v) => v.cells)) {
    if (cell.state === "grey" || cell.costPence == null) continue;
    return {
      oursPence: cell.costPence,
      tharsternPence: cell.tharsternPence ?? cell.costPence,
    };
  }
  return null;
}

// Tharstern's own product-type names for every product type these fixtures
// use — mirrors `manufacturing-setup.ts`'s `PRODUCT_TYPES` names exactly
// (same "Read it as Henry" consistency rule as `COST_CENTRE_SITE` above) so
// the dictionary index and the Live costing items table never disagree with
// Manufacturing setup about what a product type is called.
const PRODUCT_TYPE_NAMES: Record<number, string> = {
  1: "Brochure Cover & Text",
  36: "Perfect Bound Book",
  44: "Wide format panels",
  63: "Business cards",
  77: "Letterheads",
  91: "Greeting cards",
};

function dictionaryItemSelf(dictionaryItemId: string): string {
  return `/dictionary/manage/items/${dictionaryItemId}`;
}

function liveCostingChecksUrlForProductType(productTypeId: number): string {
  return `/views/live-costing/cost-lines?product_type_id=${productTypeId}`;
}

function indexEntryFromFixture(
  f: DictionaryValidationFixture,
): DictionaryValidationIndexEntry {
  return {
    _self: dictionaryItemSelf(f.dictionaryItemId),
    type: "dictionary_item",
    checksUrl: liveCostingChecksUrlForProductType(f.productTypeId),
    dictionaryItemId: f.dictionaryItemId,
    name: f.name,
    productTypeId: f.productTypeId,
    productTypeName: PRODUCT_TYPE_NAMES[f.productTypeId] ?? f.name,
    costCheckState: f.costCheckState,
    mismatchingCostLine: f.mismatchingCostLine,
    pennyExactPct: f.pennyExactPct,
    within5PctPct: f.within5PctPct,
    lastCheckedAt: f.lastCheckedAt,
    rollingChecksCount: countRollingSetCells(f),
    quarantinedPriceCount: quarantinedPricesFor(f.dictionaryItemId).length,
    usedByCount: f.usedByCount,
    lastCheck: lastCheckForFixture(f),
    // Every fixture in this older cost-engine-validation mock predates the
    // fulfilment-route feature and models a made-in-house item only —
    // `mock-data/fulfilment-routes.ts` is the one that models a real
    // multi-route coverage picture, including supplier-only items.
    isMadeInHouse: true,
    routeCoverage: {
      dictionaryItemId: f.dictionaryItemId,
      availableRouteCount: 1,
      target: 3,
      routes: [
        {
          route: { code: "in_house", label: "In-house" },
          isAvailable: true,
          reason: null,
        },
      ],
    },
  };
}

const SORT_KEY_FNS: Record<string, (f: DictionaryValidationFixture) => string> =
  {
    name: (f) => f.name,
    product_type_id: (f) => String(f.productTypeId).padStart(6, "0"),
    cost_check_state: (f) => f.costCheckState,
  };

export function listMockDictionaryValidationItems(
  options: DictionaryValidationListOptions,
): ListResponse<DictionaryValidationIndexEntry> {
  let items = [...FIXTURES];

  if (options.q) {
    const needle = options.q.toLowerCase();
    items = items.filter((f) => f.name.toLowerCase().includes(needle));
  }
  if (options.productTypeId != null) {
    items = items.filter((f) => f.productTypeId === options.productTypeId);
  }
  if (options.costCheckState) {
    items = items.filter((f) => f.costCheckState === options.costCheckState);
  }

  const tokens = (options.sort ?? "name")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  items = [...items].sort((a, b) =>
    a.dictionaryItemId.localeCompare(b.dictionaryItemId),
  );
  for (const token of [...tokens].reverse()) {
    const desc = token.startsWith("-");
    const fieldName = token.replace(/^[+-]/, "");
    const keyFn = SORT_KEY_FNS[fieldName];
    if (!keyFn) throw new Error(`unknown sort field: ${fieldName}`);
    items = [...items].sort((a, b) => {
      const ka = keyFn(a);
      const kb = keyFn(b);
      const cmp = ka < kb ? -1 : ka > kb ? 1 : 0;
      return desc ? -cmp : cmp;
    });
  }

  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? 25;
  const total = items.length;
  const start = (page - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize).map(indexEntryFromFixture),
    total,
    page,
    pageSize,
    totalPages: total === 0 ? 0 : Math.ceil(total / pageSize),
  };
}

export function getMockDictionaryValidationFeedsStatus(): DictionaryValidationFeedsStatus {
  // Must equal the number of prices actually carrying a rolling-set origin
  // across every fixture — a header-card total that doesn't match the
  // rolling validation page's own "by item" view is exactly the kind of
  // cross-page inconsistency this fixture must not have. A previous
  // version padded this with an unmodelled "wider dictionary" constant to
  // force the header card's over-budget "N will wait" wording; that number
  // wasn't backed by any real row, so drop it — at this small a set size
  // the honest state is simply under budget.
  const rollingSetSize = FIXTURES.flatMap((f) =>
    f.variants.flatMap((v) => v.cells),
  ).filter((c) => c.origin != null).length;
  return {
    rollingValidation: {
      feed: "rolling_validation",
      healthy: true,
      lastRunAt: rollingSliceAt(-1), // this hour's slice
      mintsUsedThisWeek: 24,
      mintBudgetThisWeek: 30,
      // A scoped run an admin asked for from a terminal
      // (`cis cost-check run --item dict-brochure --budget 10`). Its mints are
      // part of the 24 above — the weekly allowance is one pot.
      scopedRunsThisWeek: [
        {
          runId: "run-scoped-1",
          scope: "item:dict-brochure-cover-text",
          startedAt: isoDaysAgo(1, 11, 0),
          mintsAttempted: 6,
          pricesChecked: 6,
          outcome: "completed",
        },
      ],
      nextScheduledAt: rollingSliceAt(0), // the next hourly slice
    },
    rollingSetSize,
    quarantinedPriceCount: [...quarantinedByItem.values()].flat().length,
    checkWindowWeeks: CHECK_WINDOW_WEEKS,
  };
}

/** All checks for a cost line — capped to 12 (the drill-in list length; the
 *  reported `checkCount` on the index still carries the real total). Every
 *  miss within that cap gets an explicit or evenly-split Δ, so the board's
 *  money summary and the drill-in list are always the same source — never a
 *  hand-maintained twin that can drift. */
function checksForCostLine(fixture: CostLineFixture): CostLineCheck[] {
  if (fixture.checkSeeds.length === 0) return [];
  const count = Math.min(fixture.totalCheckCount, 12);
  const aggregateDeltaPence =
    (fixture.ourMoneyPence ?? 0) - (fixture.tharsternMoneyPence ?? 0);
  const missDeltas =
    fixture.missDeltasPence ??
    Array.from(
      { length: fixture.missCount },
      () =>
        -Math.round(
          Math.abs(aggregateDeltaPence) / Math.max(fixture.missCount, 1),
        ),
    );
  const checks: CostLineCheck[] = [];
  for (let i = 0; i < count; i++) {
    const seed = fixture.checkSeeds[i % fixture.checkSeeds.length];
    const isMiss = i < fixture.missCount;
    const deltaPence = isMiss ? missDeltas[i] : 0;
    const isAnswer = i % 3 === 0;
    // Every third check came from an in-house answer (P16); the rest are the
    // historic weekly feed's. Neither has a checkout behind it.
    const raisedBy = null;
    const checkedAt = isoDaysAgo(i, 9 + (i % 6), 0);
    const orderedAt = null;
    const quantity = [250, 500, 1000][i % 3];
    const checkId = `check-${costLineFixtureKey(fixture.costLine)}-${i}`;
    checks.push({
      _self: costCheckSelf(checkId),
      type: "cost_check",
      checkId,
      feed: isAnswer ? "in_house_answer" : "rolling_validation",
      checkedAt,
      productTypeId: seed.productTypeId,
      dictionaryItemId: seed.dictionaryItemId,
      dictionaryItemName: seed.dictionaryItemName,
      variantId: seed.variantId,
      variantLabel: seed.variantLabel,
      quantity,
      ourValuePence: 10000 + i * 137,
      tharsternValuePence: 10000 + i * 137 - deltaPence,
      deltaPence,
      result: isMiss ? fixture.health : "green",
      note: isMiss ? "Diverges from Tharstern's workings on this line" : null,
      raisedBy,
      orderedAt,
      // Before the estate unlocks, most checks are tharstern_recorded (price_spec
      // refused, so the ledger recorded
      // Tharstern's own route) — every third check is ours_forced instead,
      // so a fixture with 3+ checks shows both and a short one stays
      // honestly all-tharstern_recorded (spec 03 §4a, spec 07 §8.5/§10).
      routeSource: i % 3 === 2 ? "ours_forced" : "tharstern_recorded",
    });
  }
  return checks;
}

function summariseCostLineMoney(checks: CostLineCheck[]): CostLineMoneySummary {
  const differing = checks.filter((c) => c.deltaPence !== 0);
  const overs = differing.filter((c) => c.deltaPence > 0);
  const unders = differing.filter((c) => c.deltaPence < 0);
  return {
    differenceTotalPence: differing.reduce((sum, c) => sum + c.deltaPence, 0),
    differingCheckCount: differing.length,
    largestOverPence: overs.length
      ? Math.max(...overs.map((c) => c.deltaPence))
      : null,
    largestOverCount: overs.length,
    largestUnderPence: unders.length
      ? Math.min(...unders.map((c) => c.deltaPence))
      : null,
    largestUnderCount: unders.length,
  };
}

function affectedItemsFor(fixture: CostLineFixture): CostLineAffectedItem[] {
  const seen = new Map<string, CostLineAffectedItem>();
  for (const seed of fixture.checkSeeds) {
    if (!seen.has(seed.dictionaryItemId)) {
      seen.set(seed.dictionaryItemId, {
        dictionaryItemId: seed.dictionaryItemId,
        name: seed.dictionaryItemName,
        productTypeId: seed.productTypeId,
      });
    }
  }
  return [...seen.values()];
}

export function listMockValidationCostLines(): ListResponse<CostLineIndexEntry> {
  const items: CostLineIndexEntry[] = COST_LINE_FIXTURES.map((f) => ({
    _self: costLineSelf(f.costLine),
    type: "cost_line",
    checksUrl: costLineChecksUrl(f.costLine),
    costCentreUrl: costCentreUrlFor(f.costLine.costCentreId),
    costLine: f.costLine,
    health: f.health,
    productTypesAffected: f.productTypesAffected,
    checkCount: f.totalCheckCount,
    missCount: f.missCount,
    ourMoneyPence: f.ourMoneyPence,
    tharsternMoneyPence: f.tharsternMoneyPence,
    moneySummary: summariseCostLineMoney(checksForCostLine(f)),
    lastCheckedAt: f.lastCheckedAt,
    affectedDictionaryItems: affectedItemsFor(f),
    standardsRows: f.standardsRows,
  }));
  // Worst-first: mismatch, then small differences, then not yet checked,
  // then matches — the board's whole point is surfacing what needs attention.
  const severity: Record<CostCheckState, number> = {
    red: 0,
    amber: 1,
    grey: 2,
    green: 3,
  };
  items.sort((a, b) => severity[a.health] - severity[b.health]);
  return {
    items,
    total: items.length,
    page: 1,
    pageSize: items.length,
    totalPages: 1,
  };
}

export function getMockValidationCostLine(
  costCentreId: number,
  component: string,
): CostLineIndexEntry | null {
  const fixture = costLineFixturesByKey.get(`${costCentreId}:${component}`);
  if (!fixture) return null;
  return {
    _self: costLineSelf(fixture.costLine),
    type: "cost_line",
    checksUrl: costLineChecksUrl(fixture.costLine),
    costCentreUrl: costCentreUrlFor(fixture.costLine.costCentreId),
    costLine: fixture.costLine,
    health: fixture.health,
    productTypesAffected: fixture.productTypesAffected,
    checkCount: fixture.totalCheckCount,
    missCount: fixture.missCount,
    ourMoneyPence: fixture.ourMoneyPence,
    tharsternMoneyPence: fixture.tharsternMoneyPence,
    moneySummary: summariseCostLineMoney(checksForCostLine(fixture)),
    lastCheckedAt: fixture.lastCheckedAt,
    affectedDictionaryItems: affectedItemsFor(fixture),
    standardsRows: fixture.standardsRows,
  };
}

export function listMockCostLineChecks(
  costCentreId: number,
  component: string,
): CostLineCheck[] {
  const fixture = costLineFixturesByKey.get(`${costCentreId}:${component}`);
  if (!fixture) return [];
  return checksForCostLine(fixture);
}

// ── Live costing, all items — Settings › Manufacturing › Live costing ────
// Estate-wide status over the SAME fixtures as the dictionary index above —
// never a second, parallel item-state model (see
// `manufacturing-setup-types.ts`'s `LiveCostingEstateDetail`).

export function getMockLiveCostingEstate(): LiveCostingEstateDetail {
  return {
    _self: "/views/live-costing/estate",
    type: "live_costing_estate",
    items: [...FIXTURES]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(indexEntryFromFixture),
  };
}

/** What the catalogue price preview reads of a costed price: whether the last
 *  check at it passed, and the press(es) the engine chose with the reason. A
 *  price without a fixture cell has none (the preview then says nothing). */
export interface MockPriceCosting {
  state: CostCheckState | "checking";
  costCentreReasons: Record<number, CostCentreReason>;
  pressesConsidered: PressConsidered[];
}

function findCell(
  fixture: DictionaryValidationFixture,
  variantId: string,
  quantity: number,
): { variant: VariantSpec; cell: CellSpec } | null {
  const variant = fixture.variants.find(
    (v) => v.variantId === variantId && !v.fixedCostOptions?.length,
  );
  const cell = variant?.cells.find((c) => c.quantity === quantity);
  return variant && cell ? { variant, cell } : null;
}

export function getMockPriceCosting(
  dictionaryItemId: string,
  variantId: string,
  quantity: number,
): MockPriceCosting | null {
  const fixture = fixturesById.get(dictionaryItemId);
  const found = fixture ? findCell(fixture, variantId, quantity) : null;
  if (!fixture || !found) return null;
  return {
    state: found.cell.state,
    costCentreReasons: fixture.costCentreReasons,
    pressesConsidered: fixture.pressesConsidered,
  };
}

/** Every item's costing reasons, for the consistency test that checks them
 *  against the Setup mock. */
export function listMockItemCostings(): Pick<
  MockPriceCosting,
  "costCentreReasons" | "pressesConsidered"
>[] {
  return FIXTURES.map((f) => ({
    costCentreReasons: f.costCentreReasons,
    pressesConsidered: f.pressesConsidered,
  }));
}

// ── Rolling validation page's roll-up views ───────────────────────────────
// "By cost centre" and "by product type" over the same check ledger as "by
// item"/"by cost line" (`COST_LINE_FIXTURES`) — decision 12 Sep,
// `03_manufacturing_surfaces_design.md` "setup visibility and costing
// validation are separate". Curating rolling-set membership itself moved to
// the item matrix's Check now popover (`setMockCellRollingValidationPin`);
// there is no longer a standalone flat list of set members.

/** Site per cost centre — matches `mock-data/manufacturing-setup.ts`'s own
 *  cost-centre snapshot exactly so the two pages never disagree about where
 *  a press sits (same "Read it as Henry" consistency rule as the cost-line
 *  registry above). */
const COST_CENTRE_SITE: Record<number, string> = {
  5351: "EXETER01",
  803: "EXETER01",
  5830: "BUCKINGHAM01",
  1117: "EXETER01",
  5627: "BUCKINGHAM01",
  801: "EXETER01",
  835: "EXETER01",
  5607: "BUCKINGHAM01",
  5609: "BUCKINGHAM01",
};

/** One row per cost CENTRE, not per cost line — a cost centre can carry
 *  more than one component (5351 runs both an Ink material line and its
 *  own Print run line), and the by-cost-centre view groups them, summing
 *  each state's count across every line on that centre (coordinator
 *  review round 6; regression-tested by
 *  `cost-engine-validation.test.ts`'s "groups by cost centre" case). */
export function listMockValidationByCostCentre(): ListResponse<CostCentreValidationIndexEntry> {
  const byCostCentre = new Map<number, CostLineFixture[]>();
  for (const fixture of COST_LINE_FIXTURES) {
    const id = fixture.costLine.costCentreId;
    const list = byCostCentre.get(id) ?? [];
    list.push(fixture);
    byCostCentre.set(id, list);
  }
  const items: CostCentreValidationIndexEntry[] = [
    ...byCostCentre.entries(),
  ].map(([costCentreId, lines]) => {
    const first = lines[0].costLine;
    const lastCheckedDates = lines
      .map((l) => l.lastCheckedAt)
      .filter((d): d is string => d != null)
      .sort();
    return {
      _self: costCentreSetupUrl(costCentreId),
      type: "cost_centre",
      costCentreId,
      costCentreCode: first.costCentreCode,
      costCentreName: first.costCentreName,
      site: COST_CENTRE_SITE[costCentreId] ?? "EXETER01",
      priceChecksUsedIn: lines.reduce((sum, l) => sum + l.totalCheckCount, 0),
      lastCheckedAt: lastCheckedDates[lastCheckedDates.length - 1] ?? null,
      matchesCount: lines.filter((l) => l.health === "green").length,
      smallDifferencesCount: lines.filter((l) => l.health === "amber").length,
      mismatchCount: lines.filter((l) => l.health === "red").length,
    };
  });
  items.sort((a, b) => a.costCentreId - b.costCentreId);
  return {
    items,
    total: items.length,
    page: 1,
    pageSize: items.length,
    totalPages: 1,
  };
}

export function listMockValidationByProductType(): ListResponse<ProductTypeValidationIndexEntry> {
  const byProductType = new Map<number, DictionaryValidationFixture[]>();
  for (const fixture of FIXTURES) {
    const list = byProductType.get(fixture.productTypeId) ?? [];
    list.push(fixture);
    byProductType.set(fixture.productTypeId, list);
  }
  const items: ProductTypeValidationIndexEntry[] = [...byProductType.entries()]
    .map(([productTypeId, fixtures]) => {
      const allCells = fixtures.flatMap((f) =>
        f.variants.flatMap((v) => v.cells),
      );
      const checked = allCells.filter((c) => c.state !== "grey");
      const lastCheckedAt = checked.length > 0 ? isoDaysAgo(2) : null;
      return {
        _self: `/views/manufacturing/product-types/${productTypeId}`,
        type: "product_type",
        productTypeId,
        productTypeName: fixtures[0].name,
        itemCount: fixtures.length,
        pricesCheckedInWindow: checked.length,
        matchesCount: allCells.filter((c) => c.state === "green").length,
        smallDifferencesCount: allCells.filter((c) => c.state === "amber")
          .length,
        mismatchCount: allCells.filter((c) => c.state === "red").length,
        notYetCheckedCount: allCells.filter((c) => c.state === "grey").length,
        lastCheckedAt,
      };
    })
    .sort((a, b) => a.productTypeId - b.productTypeId);
  return {
    items,
    total: items.length,
    page: 1,
    pageSize: items.length,
    totalPages: 1,
  };
}

// ── Check settings ───────────────────────────────────────────────────────

let checkSettings: CheckSettings = {
  matchesTolerancePct: 1,
  mismatchLimitPct: 10,
  sameDirectionCount: 3,
  sameDirectionWindow: 5,
  checkWindowWeeks: CHECK_WINDOW_WEEKS,
  weeklyCheckBudget: 30,
  // cost-engine-productionisation spec 03 §4 defaults; spec 07 places these
  // on the Check settings panel (an "Order guard" group + the control
  // budget beside the weekly one - see 00_build_plan.md §07 P-1).
  orderGuardPct: 10,
  orderGuardFloorPence: 500,
  // The line floor (product owner, 19 Sep 2026): a line differing by less
  // than this many pence never trips a check.
  lineFloorPence: 10,
  // Rolling-run pacing (Ben, 24 Sep 2026): one mint a minute, 60 a run.
  mintIntervalSeconds: 60,
  runMintCap: 60,
  // Back-off after a Tharstern timeout (Ben, 24 Sep 2026): ten minutes.
  timeoutBackoffSeconds: 600,
  lastChangedBy: "Ben Pollard",
  lastChangedAt: isoDaysAgo(14, 10, 0),
  lastChangedNote: "Initial parameters, matching the accuracy model in doc 63.",
};

export function getMockCheckSettings(): CheckSettings {
  return checkSettings;
}

export function updateMockCheckSettings(
  request: CheckSettingsUpdateRequest,
  changedBy: string,
): CheckSettings {
  if (!request.note.trim()) {
    throw new Error("A note is required to change the check settings.");
  }
  checkSettings = {
    matchesTolerancePct: request.matchesTolerancePct,
    mismatchLimitPct: request.mismatchLimitPct,
    sameDirectionCount: request.sameDirectionCount,
    sameDirectionWindow: request.sameDirectionWindow,
    checkWindowWeeks: request.checkWindowWeeks,
    weeklyCheckBudget: request.weeklyCheckBudget,
    orderGuardPct: request.orderGuardPct,
    orderGuardFloorPence: request.orderGuardFloorPence,
    lineFloorPence: request.lineFloorPence,
    mintIntervalSeconds: request.mintIntervalSeconds,
    runMintCap: request.runMintCap,
    timeoutBackoffSeconds: request.timeoutBackoffSeconds,
    lastChangedBy: changedBy,
    lastChangedAt: new Date().toISOString(),
    lastChangedNote: request.note,
  };
  return checkSettings;
}

// ── Supersede checks ────────────────────────────────────────────────────────
//
// The mock keeps a small per-scope count of "checks an admin could still
// supersede", so the panel's preview → confirm → audit-list loop behaves the
// way it does against the real ledger: a count that drops to zero once the
// batch is superseded, and an audit row appended each time.

let mockSupersedableChecks: Record<string, number> = {
  all: 255,
  "product_type:33": 82,
  "dictionary_item:dict-perfect-bound-book": 14,
  "cost_centre:5351": 41,
};

let mockSupersedeEvents: SupersedeEvent[] = [
  {
    id: "evt-mock-1",
    scopeKind: "product_type",
    scopeValue: "36",
    checkedBefore: isoDaysAgo(2, 9, 0),
    reason: "engine build 122873eb4 deployed — bound-product sizing fix",
    supersededBy: "Ben Pollard",
    supersededAt: isoDaysAgo(2, 9, 5),
    checksSuperseded: 37,
    itemsAffected: 6,
  },
];

function supersedeScopeKey(request: SupersedeChecksRequest): string {
  return request.scopeKind === "all"
    ? "all"
    : `${request.scopeKind}:${request.scopeValue ?? ""}`;
}

/** Mirrors the action's own validation so the mock rejects exactly what the
 *  API rejects (cost_check_module `supersede_cost_checks._validate`). */
function assertSupersedeRequestValid(request: SupersedeChecksRequest): void {
  if (!request.reason.trim()) {
    throw new Error("A reason is required to supersede checks.");
  }
  if (request.scopeKind === "all" && request.scopeValue != null) {
    throw new Error("scopeValue must be omitted when scopeKind is 'all'.");
  }
  if (request.scopeKind !== "all" && !request.scopeValue?.trim()) {
    throw new Error(
      `scopeValue is required when scopeKind is '${request.scopeKind}'.`,
    );
  }
}

export function previewMockSupersedeChecks(
  request: SupersedeChecksRequest,
): SupersedePreview {
  assertSupersedeRequestValid(request);
  const checkCount = mockSupersedableChecks[supersedeScopeKey(request)] ?? 0;
  // Roughly six checks per item in the fixtures, and never more items than
  // checks — the panel only ever renders these as "N checks on M items".
  return {
    checkCount,
    itemCount: Math.min(checkCount, Math.ceil(checkCount / 6)),
  };
}

export function supersedeMockChecks(
  request: SupersedeChecksRequest,
  supersededBy: string,
): SupersedeChecksResult {
  const preview = previewMockSupersedeChecks(request);
  const key = supersedeScopeKey(request);
  mockSupersedableChecks = { ...mockSupersedableChecks, [key]: 0 };
  if (request.scopeKind === "all") {
    mockSupersedableChecks = Object.fromEntries(
      Object.keys(mockSupersedableChecks).map((k) => [k, 0]),
    );
  }
  const now = new Date().toISOString();
  const event: SupersedeEvent = {
    id: `evt-mock-${mockSupersedeEvents.length + 1}`,
    scopeKind: request.scopeKind,
    scopeValue: request.scopeValue ?? null,
    checkedBefore: request.checkedBefore ?? now,
    reason: request.reason.trim(),
    supersededBy,
    supersededAt: now,
    checksSuperseded: preview.checkCount,
    itemsAffected: preview.itemCount,
    // The mock has no background task queue, so the re-evaluation follow-on
    // is simulated as already finished -- the real API leaves these null
    // until its background task completes; the panel doesn't poll for them.
    reEvaluatedAt: now,
    itemsReEvaluated: preview.itemCount,
    itemsAutoPaused: 0,
  };
  mockSupersedeEvents = [event, ...mockSupersedeEvents];
  return {
    event,
    itemsQueuedForReEvaluation: preview.itemCount,
  };
}

export function listMockSupersedeEvents(): SupersedeEvent[] {
  return mockSupersedeEvents;
}
